'use client'

import React, {
  KeyboardEvent,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslations } from 'next-intl'
import { Button, TextArea, Toast } from '@skop-app/lib-front'
import { Review } from '@skop-app/lib-domain/types'
import { ReviewStatus } from '@skop-app/lib-types'

import { useReviewComposer, useSubmitReviewMessage } from '@/hooks'
import { ReviewTarget } from '@/types'

type MessageInputProps = {
  review?: Review
  target?: ReviewTarget
  onCancel?: () => void
  autoFocus?: boolean
}

export const MessageInput = React.memo(function MessageInput({
  onCancel,
  review,
  target,
  autoFocus = true,
}: MessageInputProps) {
  const t = useTranslations()
  const { getSuggestions, getLastToken, extractMentions, replaceLastToken } =
    useReviewComposer()
  const { handleSubmit } = useSubmitReviewMessage()

  // --- UI state ---
  const [message, setMessage] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // --- refs ---
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const submitLockRef = useRef(false)

  // Focus the textarea when component mounts
  useEffect(() => {
    if (!autoFocus) return
    textareaRef.current?.focus()
  }, [autoFocus])

  // --- derived values ---
  const deferredMessage = useDeferredValue(message)

  // Using hook functions - optimized for React Compiler
  const lastToken = getLastToken(deferredMessage)
  const suggestions = getSuggestions(deferredMessage)

  const showSuggestions = lastToken.length > 0

  // --- handlers ---
  const handleReplaceLastToken = (username: string) => {
    setMessage((prev) => replaceLastToken(prev, username))
    setSelectedIdx(0)
    textareaRef.current?.focus()
  }

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    setMessage(e.target.value)

  const handleMouseDown = (
    e: React.MouseEvent<HTMLButtonElement>,
    username: string,
  ) => {
    e.preventDefault() // prevent textarea blur
    handleReplaceLastToken(username)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions) return

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIdx((i) => (i > 0 ? i - 1 : suggestions.length - 1))
        break
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIdx((i) => (i < suggestions.length - 1 ? i + 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (suggestions[selectedIdx]) {
          handleReplaceLastToken(suggestions[selectedIdx].label)
        }
        break
      case 'Escape':
        e.preventDefault()
        // hide by clearing query
        setMessage((prev) => prev.trimEnd() + ' ') // no-op but keeps UX
        break
    }
  }

  const clearDraft = () => {
    setMessage('')
    setSelectedIdx(0)
  }

  const closeInput = () => {
    if (onCancel) {
      onCancel()
      return
    }

    clearDraft()
  }

  const restoreDraft = (draft: string) => {
    setMessage(draft)
    setSelectedIdx(0)
  }

  const onSubmit = async () => {
    if (!message.trim() || isSubmitting || submitLockRef.current) return

    const submittedMessage = message

    try {
      submitLockRef.current = true
      setIsSubmitting(true)
      const mentions = extractMentions(submittedMessage)
      await handleSubmit(
        {
          review,
          message: submittedMessage,
          mentions,
          target,
        },
        {
          onOptimisticApplied: clearDraft,
          onRollback: () => restoreDraft(submittedMessage),
        },
      )
      if (onCancel) {
        onCancel()
      }
    } catch (error) {
      console.error(error)
      Toast.error(t('Common.error'))
    } finally {
      submitLockRef.current = false
      setIsSubmitting(false)
    }
  }

  const hasStartedReview =
    !!review && !!review.messages?.items && review.messages.items.length > 0
  const isClosedStartedThread =
    hasStartedReview && review?.status && review.status !== ReviewStatus.PENDING
  const submitTitle = hasStartedReview
    ? isClosedStartedThread
      ? t('Review.reopen')
      : t('Common.answer')
    : t('Common.comment')

  return (
    <div className="flex w-full flex-col gap-1">
      <TextArea
        ref={textareaRef}
        aria-label={t('Review.placeholder')}
        placeholder={t('Review.placeholder')}
        value={message}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="relative">
          <ul className="absolute left-0 z-50 flex max-h-56 w-full flex-col items-start overflow-auto rounded border bg-white shadow-lg">
            {suggestions.map((user, idx) => (
              <button
                key={user.value}
                onMouseDown={(e) => handleMouseDown(e, user.label)}
                className={`w-full cursor-pointer p-2 text-sm ${
                  idx === selectedIdx ? 'bg-gray-100' : 'hover:bg-gray-100'
                }`}
              >
                @{user.label}
              </button>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button size="tiny" onClick={closeInput} variant="secondary">
          {t('Common.close')}
        </Button>
        <Button
          size="tiny"
          onClick={onSubmit}
          disabled={!message.trim() || isSubmitting}
        >
          {submitTitle}
        </Button>
      </div>
    </div>
  )
})
